import { App } from './ps20apps';
import {
  APP_TYPE_MAPPING_RULES,
  AppTypeMapper,
  AppSelectionContext,
  AppTypeMappingRule,
} from './app-type-mapper';
import { FeatureToggles } from './featureToggles';

/**
 * Central application type configuration entry point.
 * All equivalence groups, selection logic and future extension points are wired here.
 */
export interface AppTypeConfigOptions {
  featureToggles?: FeatureToggles;
  additionalRules?: AppTypeMappingRule[];
}

export class AppTypeConfigService {
  private readonly mapper: AppTypeMapper;

  constructor(options: AppTypeConfigOptions = {}) {
    const rules = [...APP_TYPE_MAPPING_RULES, ...(options.additionalRules || [])];
    this.mapper = new AppTypeMapper(rules);
    this.options = options;
  }

  private options: AppTypeConfigOptions;

  /** Return active app normalized by context (feature toggles, sourceAppType, beibehalten) */
  normalize(app: App, additionalContext?: Partial<AppSelectionContext>): App {
    return this.mapper.normalizeApp(app, this.buildContext(additionalContext));
  }

  /** Check if two apps are equivalent */
  equivalent(a: App, b: App): boolean {
    return this.mapper.areEquivalent(a, b);
  }

  /** Expand list with all equivalents */
  expand(apps: App[]): App[] {
    return this.mapper.expandAppsWithEquivalents(apps);
  }

  /** Is app in list considering equivalence */
  inList(app: App, list: App[]): boolean {
    return this.mapper.isAppInList(app, list);
  }

  /** Active app to use for a given representative, considering context */
  active(app: App, additionalContext?: Partial<AppSelectionContext>): App {
    return this.mapper.getActiveApp(app, this.buildContext(additionalContext));
  }

  /** Get equivalents for single app */
  equivalents(app: App): App[] {
    return this.mapper.getEquivalentApps(app);
  }

  private buildContext(additionalContext?: Partial<AppSelectionContext>): AppSelectionContext {
    return {
      featureToggles: this.options.featureToggles || {},
      ...additionalContext,
    };
  }
}

// Singleton (can be parameterized later if needed)
export const appTypeConfig = new AppTypeConfigService();

// Convenience re-exports for existing helpers (migration layer)
export const normalizeAppType = (app: App, context?: Partial<AppSelectionContext>): App =>
  appTypeConfig.normalize(app, context);
export const areAppTypesEquivalent = (a: App, b: App): boolean => appTypeConfig.equivalent(a, b);
export const expandAppTypes = (apps: App[]): App[] => appTypeConfig.expand(apps);
export const isAppTypeInList = (app: App, list: App[]): boolean => appTypeConfig.inList(app, list);
export const getActiveAppType = (app: App, context?: Partial<AppSelectionContext>): App =>
  appTypeConfig.active(app, context);
export const getEquivalentAppTypes = (app: App): App[] => appTypeConfig.equivalents(app);
